import { Button, message, Space, Table, Typography } from "antd"
import {
  ArrowRightOutlined,
  CheckSquareFilled,
  CloseOutlined,
  FileOutlined,
} from "@ant-design/icons"
import "./WarehouseOrders.css"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import { startDate } from "../../../components/Orders/Helpers/TableDetails"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { useState } from "react"
import CreateInvoiceAsDrawer from "../../Invoices/Create/CreateInvoiceAsDrawer"
import { ProformaPaymentStatus } from "../../../components/Payment/ProformaPaymentStatus"

const { Text, Link } = Typography

export default function PrePayment({ data, loading }) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  //makes sure only one client is enabled for selection
  const [firstSelectedClient, setFirstSelectedClient] = useState(null)

  //selected rows
  const onSelectChange = (newSelectedRowKeys, selectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys)
    setSelectedRows(selectedRows)

    //allows to select only one kind of client
    if (selectedRows.length === 1) {
      // Sets the client when the first row is selected
      setFirstSelectedClient(selectedRows[0].client.client)
    } else if (selectedRows.length === 0) {
      // Resets when no rows are selected
      setFirstSelectedClient(null)
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
    getCheckboxProps: (record) => ({
      // This is the core logic:
      disabled:
        // Disable if a client is already selected AND the current row's client is different
        (firstSelectedClient !== null && record.client.client !== firstSelectedClient) ||
        // Also disable if the record has an invoice (same condition as in WarehouseOrders)
        record.hasInvoice,
    }),
  }

  const updateOrderFinishedPrePayment = async (order) => {
    try {
      const docRef = doc(db, "orders", order.id)
      await updateDoc(docRef, {
        orderStatus: 1,
      })
      message.success("Order updated!")
    } catch (error) {
      message.error("Error, check console.")
      console.error("Error updating Firestore document: ", error)
    }
  }

  const handleProformaDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        proformaDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }

  //handles the reset of the rowkeys
  const handleResetRows = () => {
    setSelectedRowKeys([])
    setSelectedRows([])
    setFirstSelectedClient(null)
  }

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  const returnCorrectButton = (record) => {
    return (
      <Space direction="vertical">
        {record.hasProforma ? (
          <a
            href={record.proforma.proformaUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleProformaDownload(record.id)}>
            <Button size="small" icon={<FileOutlined style={{ color: "#1677ff" }} />}>
              {record.proforma.name}
              {/* Conditionally display arrow if proformaDownloaded === true */}
              {record.proformaDownloaded && (
                <CheckSquareFilled style={{ marginLeft: 4, color: "#1677ff" }} />
              )}
            </Button>
          </a>
        ) : (
          <Text>No Document</Text>
        )}
      </Space>
    )
  }

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      width: "10%",
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Payment",
      dataIndex: "proformaPaymentStatus",
      align: "center",
      render: (value, record) => <ProformaPaymentStatus number={record.proformaPaymentStatus} />,
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      width: "15%",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      ellipsis: true,
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      width: "25%",
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "center",
      ellipsis: true,
    },

    {
      title: "Document",
      align: "center",
      width: "10%",
      render: (value, record) => {
        return returnCorrectButton(record) // Add the return keyword here
      },
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => {
        if (value) {
          return (
            <Link target="_blank" href={value}>
              Link
            </Link>
          )
        } else {
          return <CloseOutlined />
        }
      },
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
    },
    {
      title: "Started",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => startDate(value),
    },
    {
      title: "Done",
      align: "center",
      render: (value, record) => (
        <Button
          size="small"
          icon={<ArrowRightOutlined />}
          type="primary"
          disabled={record.proformaPaymentStatus !== 2}
          onClick={() => updateOrderFinishedPrePayment(record)}>
          Forward
        </Button>
      ),
    },
  ]

  return (
    <div>
      <Space direction="vertical">
        <CreateInvoiceAsDrawer
          selectedRows={selectedRows}
          selectedRowKeys={selectedRowKeys}
          resetRows={handleResetRows}
          forProforma={true}
        />
        <Table
          style={{ marginTop: "15px" }}
          size="small"
          loading={loading}
          columns={defaultColumns}
          bordered
          dataSource={data}
          rowKey={(record) => record.id}
          rowSelection={rowSelection}
          pagination={{ defaultPageSize: "12", showSizeChanger: true }}
        />
      </Space>
    </div>
  )
}
